import { AppProps } from "next/app";
import Head from "next/head";
import { Provider } from "react-redux";
import { portalStoreWrapper } from "@ecp-boxes/redux/store";
import useIsMobilePortal from "@ecp-boxes/shared/hooks/useIsMobilePortal";
import useSetTenant from "@ecp-boxes/shared/hooks/useSetTenant";
import * as theme from "@ecp-redux/api/themeSettings";
import "react-toastify/dist/ReactToastify.css";
import { useEffect } from "react";
import Cookies from "js-cookie";
import dynamic from "next/dynamic";

const DynamicToastContainer = dynamic(
  () =>
    import("@ecp-boxes/shared/components/Snackbar/Snackbar.styled").then(
      (mod) => mod.StyledPortalToastContainer
    ),
  { ssr: false }
);

async function initMocks() {
  if (typeof window === "undefined" || process.env.NODE_ENV === "test") {
    /* eslint-disable-next-line @typescript-eslint/no-var-requires */
    const { server } = await import("@ecp-redux/mocks/msw/server");
    server !== undefined && server.listen();
  } else {
    /* eslint-disable-next-line @typescript-eslint/no-var-requires */
    const { worker } = await import("@ecp-redux/mocks/msw/browser");
    worker !== undefined &&
      worker.start({
        onUnhandledRequest: "bypass",
      });
  }
}

if (process.env.NODE_ENV !== "production") {
  // initMocks();
}

const GTMScript: React.FC = (): JSX.Element => {
  const isMobile = useIsMobilePortal();
  const { data: themeSettings } = theme.useGetThemeSettingsPortalQuery(
    isMobile ? "MOBILE" : "DESKTOP"
  );

  useEffect(() => {
    const { collect_events = "true" } =
      themeSettings?.advanceSettings?.settings ?? {};
    sessionStorage.setItem("collect_events", collect_events);
  }, [themeSettings]);

  const customGTMScript = themeSettings?.advanceSettings?.trackingScript;
  return (
    <>
      {customGTMScript && (
        <Head>
          <script
            nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
            id="Google Task Manager"
            dangerouslySetInnerHTML={{
              __html: customGTMScript,
            }}
          />
        </Head>
      )}
    </>
  );
};

const CustomFavicon: React.FC = (): JSX.Element => {
  const isMobile = useIsMobilePortal();
  const { data: themeSettings } = theme.useGetThemeSettingsPortalQuery(
    isMobile ? "MOBILE" : "DESKTOP"
  );

  const customFaviconUrl = themeSettings?.advanceSettings.settings.faviconURL;

  return (
    <Head>
      <link rel="icon" href={customFaviconUrl} />
    </Head>
  );
};

function CustomApp({ Component, ...rest }: AppProps) {
  useSetTenant();
  const { store, props } = portalStoreWrapper.useWrappedStore(rest);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      document.addEventListener("visibilitychange", function () {
        const token =
          sessionStorage.getItem("contractor-token") ??
          localStorage.getItem("contractor-token");
        if (!document.hidden && token) {
          Cookies.set("contractor-token", token, {
            path: "/",
            sameSite: "strict",
          });
        }
      });

      //prevent pinch to zoom on input fields
      window.document.addEventListener(
        "touchstart",
        function (event: any) {
          if (event.target.tagName === "INPUT") {
            const metaViewport = document.querySelector(
              "meta[name='viewport']"
            );
            if (metaViewport) {
              (metaViewport as any).content =
                "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0";
            }
          }
        },
        { passive: false }
      );
    }
  }, []);

  return (
    <Provider store={store}>
      <GTMScript />
      <CustomFavicon />
      <Component {...props.pageProps} />
      <DynamicToastContainer
        containerId="PortalSnackbar"
        position="bottom-right"
        closeButton={false}
        limit={3}
      />
    </Provider>
  );
}

export default CustomApp;
